import React, { Suspense, useState } from 'react'

import ProfilePhotoFallback from 'src/shared/assets/images/profile-avatar-fallback.png'
import { ErrorBoundaryWrapper } from 'src/shared/components/errors/error-boundary-wrapper'
import { Avatar, AvatarImage } from 'src/shared/components/ui/miscellaneous/avatar'
import { Skeleton } from 'src/shared/components/ui/miscellaneous/skeleton'
import { useUserPhoto } from 'src/shared/features/users/api/accounts/use-user-photo'
import { cn } from 'src/shared/utils/cn'

import ProfileAvatarDialog from './profile-avatar-dialog'

interface ProfileAvatarProps {
  userId: number
  size?: number
  className?: string
  onClick?: () => void
  /** If true, a popup with a larger version of the avatar will be shown when the avatar is clicked. */
  shouldZoom?: boolean
  /** If provided, this photo will be used as the fallback photo instead of the default fallback photo. */
  customFallbackPhoto?: string
  shadow?: boolean
}

export { ProfilePhotoFallback }

const ProfileAvatarContent: React.FC<ProfileAvatarProps> = ({
  userId,
  size = 40,
  className,
  onClick,
  shouldZoom = false,
  customFallbackPhoto,
}) => {
  let { data: photo } = useUserPhoto('profile_photo', userId)
  const [isDialogOpen, setIsDialogOpen] = useState(false)

  const handleClick = () => {
    if (shouldZoom) {
      setIsDialogOpen(true)
    }
    if (onClick) {
      onClick()
    }
  }

  if (!photo) {
    photo = customFallbackPhoto || ProfilePhotoFallback
  }

  const imageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover' as const,
  }

  return (
    <>
      <Avatar
        style={{ height: size, width: size }}
        className={cn(`rounded-full ${className || ''} overflow-hidden bg-accent`, shouldZoom && 'cursor-pointer')}
        onClick={handleClick}
      >
        <AvatarImage src={photo} alt={'user profile photo'} style={imageStyle} className="object-cover" />
      </Avatar>
      {shouldZoom && <ProfileAvatarDialog isOpen={isDialogOpen} setOpen={setIsDialogOpen} photo={photo} />}
    </>
  )
}

export const ProfileAvatar: React.FC<ProfileAvatarProps> = ({
  userId,
  size = 40,
  className,
  onClick,
  shouldZoom = false,
  customFallbackPhoto,
  shadow = false,
}) => {
  return (
    <ErrorBoundaryWrapper style="custom" fallback={<Skeleton className="size-4 rounded-full bg-accent" />}>
      <Suspense fallback={<Skeleton className="size-4 rounded-full bg-accent" />}>
        <ProfileAvatarContent
          userId={userId}
          size={size}
          className={cn(className, shadow && 'shadow-[0_1px_6px_1px_rgba(27,48,41,0.1)]')}
          onClick={onClick}
          shouldZoom={shouldZoom}
          customFallbackPhoto={customFallbackPhoto}
        />
      </Suspense>
    </ErrorBoundaryWrapper>
  )
}

export default ProfileAvatar
